import { useEffect, useRef, useState } from "react";
import { Languages, RotateCcw, Send } from "lucide-react";
import { QUESTIONS, BRIEF_STRINGS, composeBrief } from "../data/templates";

// Free guided Q&A that builds a generation brief — no AI, no credits.
export const BriefBuilder = ({ lang, onToggleLang, onUseBrief }) => {
  const t = BRIEF_STRINGS[lang];
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [picked, setPicked] = useState([]);
  const [finished, setFinished] = useState(null);
  const scrollRef = useRef(null);

  const ask = (idx, msgs) => {
    const q = QUESTIONS[idx];
    return [...msgs, { from: "bot", text: q[lang], key: q.key }];
  };

  useEffect(() => {
    setMessages(ask(0, [{ from: "bot", text: t.hint, key: "intro" }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, finished]);

  const restart = () => {
    setStep(0);
    setAnswers({});
    setPicked([]);
    setFinished(null);
    setInput("");
    setMessages(ask(0, [{ from: "bot", text: t.hint, key: "intro" }]));
  };

  const advance = (value) => {
    const q = QUESTIONS[step];
    const nextAnswers = { ...answers };
    if (value) nextAnswers[q.key] = value;
    const displayValue = q.multi && value ? value.split(", ").join(", ") : value;
    let msgs = [...messages];
    if (displayValue) msgs = [...msgs, { from: "me", text: displayValue, key: q.key }];
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
      setAnswers(nextAnswers);
      setMessages(ask(step + 1, msgs));
    } else {
      const brief = composeBrief(nextAnswers);
      setFinished(brief);
      setMessages([...msgs, { from: "bot", text: t.done, key: "done" }]);
    }
    setInput("");
    setPicked([]);
  };

  const current = QUESTIONS[step];
  const togglePick = (label) =>
    setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));

  const chipCls = (on) =>
    `px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
      on
        ? "bg-cyan-700 text-white border-cyan-700"
        : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-cyan-400"
    }`;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950" data-testid="brief-builder">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-800">
        <span className="text-[11px] font-bold uppercase code-font text-cyan-700 dark:text-cyan-300">{t.title}</span>
        <span className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={restart}
            className="p-1.5 rounded border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            title={t.restart}
            data-testid="brief-restart-button"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={onToggleLang}
            className="flex items-center gap-1 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:border-cyan-400 transition-colors"
            data-testid="brief-lang-toggle"
          >
            <Languages className="w-3 h-3" /> {t.langToggle}
          </button>
        </span>
      </div>

      <div ref={scrollRef} className="max-h-56 overflow-y-auto p-3 space-y-2" data-testid="brief-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-3 py-1.5 rounded-2xl text-xs leading-relaxed ${
                m.from === "me"
                  ? "bg-cyan-700 text-white rounded-br-sm"
                  : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-bl-sm"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
      </div>

      {finished ? (
        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <pre className="whitespace-pre-wrap text-[11px] text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-2 max-h-32 overflow-y-auto" data-testid="brief-result">
            {finished}
          </pre>
          <button
            type="button"
            onClick={() => onUseBrief(finished)}
            className="mt-2 w-full px-3 py-2 rounded bg-cyan-700 text-white text-xs font-bold uppercase tracking-wider hover:bg-cyan-800 transition-colors"
            data-testid="brief-use-button"
          >
            {t.use}
          </button>
        </div>
      ) : (
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 space-y-2">
          {current?.options && (
            <div className="flex flex-wrap gap-1.5" data-testid="brief-options">
              {current.options.map((o) => {
                const label = o[lang];
                const on = current.multi ? picked.includes(label) : false;
                return (
                  <button
                    key={o.en}
                    type="button"
                    onClick={() => (current.multi ? togglePick(label) : advance(label))}
                    className={chipCls(on)}
                    data-testid={`brief-option-${current.key}-${o.en.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex gap-1.5">
            <input
              className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-cyan-600 text-xs"
              placeholder={t.placeholder}
              value={current?.multi ? picked.join(", ") : input}
              onChange={(e) => !current?.multi && setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), (current?.multi ? advance(picked.join(", ")) : input.trim() && advance(input.trim())))}
              readOnly={!!current?.multi}
              data-testid="brief-answer-input"
            />
            <button
              type="button"
              onClick={() => advance(current?.multi ? picked.join(", ") : input.trim())}
              disabled={!current?.multi && !input.trim()}
              className="px-3 py-2 rounded-lg bg-cyan-700 text-white hover:bg-cyan-800 transition-colors disabled:opacity-50"
              data-testid="brief-send-button"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => advance("")}
              className="px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              data-testid="brief-skip-button"
            >
              {current?.multi ? t.next : t.skip}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
